// Make changes › buttons — "what it does" in plain words ⇄ the designer's
// ActionDef. Pure functions.
//
// Open another screen / Print a document / Send a message / Delete the record
// are declarative handlers the engine runs as they are. "Fill in fields" and
// "Show a message" run a small business-logic function in the engine; the
// button names it and states what it must do in `business_logic.spec`, and
// saving writes the code from that sentence (the designer's own save path,
// `_author_business_logic_on_save`). The sentence is also how the panel reads
// the button back.

import type { ActionDef, ActionScope } from '../../txn/types'
import { TEXT_LIT, quoteText, unquoteText } from './itemChange'

export type ButtonDoes = 'fill' | 'message' | 'open' | 'report' | 'send' | 'delete'

export const BUTTON_DOES: { id: ButtonDoes; label: string }[] = [
  { id: 'fill', label: 'Fill in fields' },
  { id: 'message', label: 'Show a message' },
  { id: 'open', label: 'Open another screen' },
  { id: 'report', label: 'Print a document' },
  { id: 'send', label: 'Send a message' },
  { id: 'delete', label: 'Delete the record' },
]

export interface ButtonSpec {
  name: string
  does: ButtonDoes
  scope: ActionScope
  /** fill: set this field … */
  field?: string
  /** … to this value. */
  value?: string
  /** message: the text ({Field name} for a value of the record). */
  message?: string
  /** open / report: the target object. */
  target?: string
  /** open: the field of this record that picks the target's record (none =
   *  open a new record there). */
  match?: { field: string; targetKey: string } | null
  /** send: the channel. */
  channel?: 'email' | 'whatsapp' | 'sms'
  /** Show it only when <field> is <value> (row buttons). */
  when?: { field: string; value: string } | null
  /** Ask "Are you sure?" first, with this question. */
  confirm?: string | null
  /** The keyboard key that runs it ("Alt+A") — never a system key
   *  (txn/actionShortcuts.json). A Delete button takes Delete's own key. */
  shortcut?: string
}

const FILL_SPEC = /^Set the field "([A-Za-z_][A-Za-z0-9_]*)" \(.*?\) to the value "((?:[^"\\]|\\.)*)"/
const MSG_SPEC = /^Show the person this message: "((?:[^"\\]|\\.)*)"/
const WHEN_RE = new RegExp(`^\\{form\\.([A-Za-z_][A-Za-z0-9_]*)\\}\\s*==\\s*(${TEXT_LIT})$`)

const esc = (s: string) => s.replace(/\\/g, '\\\\').replace(/"/g, '\\"')
const unesc = (s: string) => s.replace(/\\(.)/g, '$1')

/** The function name a button's code lives under. */
export function methodName(objName: string, buttonName: string): string {
  const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')
  return `${slug(objName) || 'screen'}_${slug(buttonName) || 'button'}`
}

export function specToAction(s: ButtonSpec, opts: {
  objName: string
  /** A field's name on the form (for the sentence the code is written from). */
  label: (field: string) => string
  /** The previous definition (kept keys: order, roles, validations …). */
  previous?: ActionDef | null
}): ActionDef {
  const prev = opts.previous || null
  const base: ActionDef = {
    ...(prev?.order !== undefined ? { order: prev.order } : {}),
    ...(prev?.validations ? { validations: prev.validations } : {}),
    ...(prev?.allow_bulk !== undefined ? { allow_bulk: prev.allow_bulk } : {}),
    ...(prev?.placement ? { placement: prev.placement } : {}),
    ...(s.shortcut && s.does !== 'delete' ? { shortcut: s.shortcut } : {}),
    name: s.name.trim(),
    handler: 'message',
    scope: s.scope,
  }
  if (s.when && s.when.field && s.when.value.trim()) {
    base.visible_when = `{form.${s.when.field}} == ${quoteText(s.when.value.trim())}`
  }
  if (s.confirm && s.confirm.trim()) base.confirmation = { mode: 'reconfirm', message: s.confirm.trim() }
  const method = prev?.business_logic?.method || methodName(opts.objName, s.name)
  switch (s.does) {
    case 'fill':
      return {
        ...base, handler: 'item_change',
        business_logic: {
          method,
          description: `${s.name}: set ${opts.label(s.field || '')}`,
          spec: `Set the field "${s.field}" (${opts.label(s.field || '')}) to the value "${esc(s.value || '')}" `
            + 'on the open record and return it as an item-change update. Nothing else changes.',
        },
      }
    case 'message':
      return {
        ...base, handler: 'message',
        business_logic: {
          method,
          description: `${s.name}: shows a message`,
          spec: `Show the person this message: "${esc(s.message || '')}". A name in braces, like {Claim no}, `
            + 'is replaced by that field\'s value on the open record. Return it as the message; change nothing.',
        },
      }
    case 'open':
      return {
        ...base, handler: 'open',
        open: {
          obj_type: 'T', obj_name: s.target || '',
          mode: s.match ? 'edit' : 'add',
          ...(s.match ? { match: { [s.match.targetKey]: `{form.${s.match.field}}` } } : {}),
          presentation: 'drawer',
          title: s.name.trim(),
        },
      }
    case 'report':
      return { ...base, handler: 'report', report: { obj_name: s.target || '', param_map: prev?.report?.param_map || {} } }
    case 'send':
      return {
        ...base, handler: 'send',
        send: { ...(prev?.send || {}), enabled: true, channels: [s.channel || 'email'], user_templates: prev?.send?.user_templates ?? true },
      }
    case 'delete':
      return { ...base, handler: 'system', system_op: 'delete' }
  }
}

/** Read a button back; null when these words cannot describe it (shown
 *  read-only, with Open in the full designer). */
export function actionToSpec(a: ActionDef): ButtonSpec | null {
  const scope: ActionScope = a.scope === 'row' ? 'row' : 'form'
  const m = String(a.visible_when || '').trim().match(WHEN_RE)
  if ((a.visible_when || '').trim() && !m) return null
  const when = m ? { field: m[1], value: unquoteText(m[2]) ?? '' } : null
  const conf = a.confirmation && a.confirmation.mode !== 'none' ? a.confirmation : null
  if (conf && conf.mode !== 'reconfirm') return null
  const confirm = conf ? (conf.message || 'Are you sure?') : null
  const base = { name: a.name, scope, when, confirm, ...(a.shortcut ? { shortcut: a.shortcut } : {}) }
  const spec = String(a.business_logic?.spec || '')
  switch (a.handler) {
    case 'item_change': {
      const f = spec.match(FILL_SPEC)
      return f ? { ...base, does: 'fill', field: f[1], value: unesc(f[2]) } : null
    }
    case 'message': {
      const f = spec.match(MSG_SPEC)
      return f ? { ...base, does: 'message', message: unesc(f[1]) } : null
    }
    case 'open': {
      const o = a.open
      if (!o || (o.obj_type && o.obj_type !== 'T') || (o.set && o.set.length) || (o.then && o.then.length)) return null
      const entries = Object.entries(o.match || {})
      const mm = entries.length === 1 ? String(entries[0][1]).match(/^\{form\.([A-Za-z_][A-Za-z0-9_]*)\}$/) : null
      if (entries.length && !mm) return null
      return { ...base, does: 'open', target: o.obj_name, match: mm ? { field: mm[1], targetKey: entries[0][0] } : null }
    }
    case 'report':
      return a.report?.obj_name ? { ...base, does: 'report', target: a.report.obj_name } : null
    case 'send': {
      const ch = a.send?.channels?.[0]
      return { ...base, does: 'send', channel: ch === 'whatsapp' || ch === 'sms' ? ch : 'email' }
    }
    case 'system':
      return a.system_op === 'delete' ? { ...base, does: 'delete' } : null
    default:
      return null
  }
}

/** The button in one sentence. */
export function buttonSentence(s: ButtonSpec, label: (f: string) => string, targetTitle: (t: string) => string): string {
  const what = s.does === 'fill' ? `sets ${label(s.field || '…')} to “${s.value || '…'}”`
    : s.does === 'message' ? `shows “${s.message || '…'}”`
    : s.does === 'open' ? `opens ${s.target ? targetTitle(s.target) : '…'}${s.match ? ` on the record in ${label(s.match.field)}` : ' with a new record'}`
    : s.does === 'report' ? `prints ${s.target ? targetTitle(s.target) : '…'}`
    : s.does === 'send' ? `sends a${s.channel === 'email' || !s.channel ? 'n email' : ` ${s.channel === 'whatsapp' ? 'WhatsApp message' : 'text message'}`} about the record`
    : 'deletes the record'
  const where = s.scope === 'row' ? 'on each record of the list and on the open record' : 'above the list and on the open record'
  const when = s.when ? `, only when ${label(s.when.field)} is “${s.when.value}”` : ''
  const ask = s.confirm ? ', after asking “' + s.confirm + '”' : ''
  const key = s.shortcut && s.does !== 'delete' ? ` Key: ${s.shortcut}.` : ''
  return `“${s.name || '…'}” ${what} — ${where}${when}${ask}.${key}`
}
